import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import Button from './Button';
import {
    HeaderContainer, HeaderLeftSection, DesktopActions,
    MobileMenuToggle, MobileMenuContainer
} from './ResponsiveHeaderStyles';
import {logout} from "../../api";
import {authURL, clientURL} from "../../lib/apiClient";
import useAuth from "../../hooks/useAuth";
import HeaderGuardian from "./HeaderGuardian";
import {useEmergencyManager} from "../../hooks/useEmergencyManager";

const Title = styled.h1` font-size: 1.3rem; font-weight: bold; white-space: nowrap; `;
const UserName = styled.span`font-weight: bold;`;
const LogoutButton = styled(Button)`
    background-color: var(--bg-list);
    &:hover { background-color: var(--border-color); }
`;

// 설치 버튼 스타일 (기존 버튼과 통일감을 주되 색상으로 강조)
const InstallButton = styled(Button)`
    background-color: #4f46e5; 
    color: white;
    &:hover { background-color: #4338ca; }
`;

// ✨ 긴급 버튼 애니메이션
const pulseRed = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(224, 108, 117, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(224, 108, 117, 0); }
  100% { box-shadow: 0 0 0 0 rgba(224, 108, 117, 0); }
`;

// ✨ 긴급 시작 버튼
const EmergencyStartBtn = styled(Button)`
    background-color: #e06c75; // Red
    color: white;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    &:hover { background-color: #be5046; }
    &:active { transform: scale(0.95); }
`;

// ✨ 긴급 종료(복귀) 버튼
const EmergencyResumeBtn = styled(Button)`
    background-color: #98c379; // Green
    color: white;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    animation: ${pulseRed} 2s infinite; // 복귀할 업무가 있다는 것을 시각적으로 강조
    &:hover { background-color: #7a9f60; }
`;

// PWA 설치 이벤트를 위한 타입 정의
interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

interface ResponsiveHeaderProps {
    title: string;
    leftContent?: React.ReactNode; // 타이틀 옆에 올 내용 (날짜 등)
    extraActions?: React.ReactNode; // 페이지별 특수 버튼 (+ 새 프로젝트 등)
}

const ResponsiveHeader: React.FC<ResponsiveHeaderProps> = ({ title, leftContent, extraActions }) => {
    const user = useAuth();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

// ✨ useEmergencyManager는 이제 서버 상태를 체크합니다.
    const { startEmergency, stopEmergency, hasActiveEmergency, isLoading } = useEmergencyManager();

    // ✨ PWA 설치 관련 상태
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };
        // 브라우저가 설치 가능함을 감지하면 이벤트 발생
        window.addEventListener('beforeinstallprompt', handler);

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    // ✨ 모든 페이지 공통 이동 버튼
    const CommonNavButtons = () => (
        <>
            <EmergencyActions />

            {extraActions}
            {deferredPrompt && (
                <InstallButton onClick={handleInstallClick}>📱 앱 설치</InstallButton>
            )}
            <Button onClick={() => navigate('/projects')}>Projects</Button>
            <Button onClick={() => navigate('/report')}>Report</Button>
            <Button onClick={() => navigate('/activity/playback')}>🎬 Replay</Button>
            <Button onClick={() => navigate('/activity')}>Log</Button>
            <Button onClick={() => navigate('/gantt')}>Gantt</Button>
            <Button onClick={() => navigate('/tasks')} cyan>All Tasks</Button>
            <Button onClick={() => navigate('/calendar')}>Calendar</Button>
            <Button onClick={() => navigate('/')}>Board View</Button>
            <Button onClick={() => navigate('/settings')}>⚙ Settings</Button>
            {user && <UserName style={{ padding: '0.5rem 1rem', color: 'var(--text-bright)' }}>{user.name}님</UserName>}
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </>
    );

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
        }
    };

    const handleLogout = async () => {
        await logout();
        const authServerLogout = authURL + "/logout";
        const finalDestination = encodeURIComponent(clientURL + "/login ");
        window.location.href = `${authServerLogout}?continue=${finalDestination}`;
    };

    // ✨ 긴급 버튼 그룹
    const EmergencyActions = () => (
        <div style={{ display: 'flex', gap: '0.5rem', marginRight: '0.5rem', borderRight: '1px solid var(--border-color)', paddingRight: '0.5rem' }}>
            {/* 서버 DB(현재 카드 description)에 '돌아갈 ID'가 있다면
               어떤 기기에서든 이 버튼이 활성화되어 보입니다.
            */}
            {hasActiveEmergency && (
                <EmergencyResumeBtn onClick={stopEmergency} disabled={isLoading}>
                    {isLoading ? '...' : '⏹ 긴급 종료'}
                </EmergencyResumeBtn>
            )}

            <EmergencyStartBtn onClick={startEmergency} disabled={isLoading}>
                {isLoading ? '...' : '🚨 긴급'}
            </EmergencyStartBtn>
        </div>
    );

    return (
            <HeaderContainer>
                <HeaderLeftSection>
                    <HeaderGuardian />
                    <Title>{title}</Title>
                    {leftContent}
                </HeaderLeftSection>

                <DesktopActions>
                    <CommonNavButtons />
                </DesktopActions>

                <MobileMenuToggle onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    ☰
                </MobileMenuToggle>

                <MobileMenuContainer isOpen={isMenuOpen}>
                    <CommonNavButtons />
                </MobileMenuContainer>
            </HeaderContainer>
    );
};

export default ResponsiveHeader;